import React, {memo, useCallback, useId} from 'react';
import {Box, Typography, useTheme} from '@mui/material';
import {AdvancedMarker} from '@vis.gl/react-google-maps';
import type {LatLng} from '../map-polygon/index.ts';
import {PlusIcon} from '../../assets/icons/plus.tsx';

export type MarkerId = string | number;
export type MarkerSize = 'small' | 'medium';

export type ContourMarkerItem = {
    id: MarkerId;
    polygonId?: string;
    position: LatLng;
    size?: MarkerSize;
    label?: string;
    labelTitle?: string;
    icon?: React.ReactNode;
    backgroundColor?: string;
    borderColor?: string;
    iconColor?: string;
    activeContent?: React.ReactNode;
    isDraggable?: boolean;
    isDisabled?: boolean;
    isLinkedToDevice?: boolean;
    linkedIcon?: React.ReactNode;
    revision?: number | string;
};

export type MarkerDragStartPayload = {
    marker: ContourMarkerItem;
    from: LatLng;
};

export type MarkerDragEndPayload = {
    marker: ContourMarkerItem;
    from: LatLng;
    droppedAt: LatLng | null;
};

type ContourMarkerProps = {
    marker: ContourMarkerItem;
    active?: boolean;
    elevated?: boolean;
    onClick?: (marker: ContourMarkerItem) => void;
    onMouseEnter?: (marker: ContourMarkerItem) => void;
    onMouseLeave?: (marker: ContourMarkerItem) => void;
    onDragStart?: (payload: MarkerDragStartPayload) => void;
    onDragEnd?: (payload: MarkerDragEndPayload) => void;
};

type MarkerSizeConfig = {
    bodyWidth: number;
    bodyHeight: number;
    iconTop: number;
    labelOverlap: number;
    strokeWidth: number;
};

const MARKER_SIZE_CONFIG: Record<MarkerSize, MarkerSizeConfig> = {
    small: {
        bodyWidth: 40,
        bodyHeight: 44,
        iconTop: 10,
        labelOverlap: 10,
        strokeWidth: 2,
    },
    medium: {
        bodyWidth: 56,
        bodyHeight: 64,
        iconTop: 16,
        labelOverlap: 12,
        strokeWidth: 3,
    },
};

const LABEL_HEIGHT = 24;
const DETAILED_LABEL_HEIGHT = 44;
const FIGMA_PIN_WIDTH = 72;
const FIGMA_PIN_HEIGHT = 76;
const FIGMA_PIN_FILTER_Y = -4;
const FIGMA_PIN_FILTER_HEIGHT = 79.6729;
const FIGMA_PIN_VISIBLE_LEFT = 16;
const FIGMA_PIN_VISIBLE_TOP = 8;
const FIGMA_PIN_VISIBLE_WIDTH = 40;
const FIGMA_PIN_VISIBLE_HEIGHT = 43.3747;
const FIGMA_PIN_PATH =
    'M56 26.3333C56 38.2624 41.0377 48.7573 36.996 51.3747C36.3816 51.7726 35.6184 51.7725 35.004 51.3747C30.9623 48.7573 16 38.2624 16 26.3333C16 16.2081 24.9543 8 36 8C47.0457 8 56 16.2081 56 26.3333Z';
const FIGMA_PIN_MASK_IMAGE = `url("data:image/svg+xml,${encodeURIComponent(
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${FIGMA_PIN_WIDTH} ${FIGMA_PIN_HEIGHT}"><path fill="white" d="${FIGMA_PIN_PATH}"/></svg>`,
)}")`;
const FIGMA_PIN_SHADOW = 'drop-shadow(0 8px 16px rgba(0, 0, 0, 0.25))';
const LEGACY_CHROMIUM_GLASS_MARKER_MAX_MAJOR_VERSION = 128;

const getChromiumMajorVersion = () => {
    if (typeof navigator === 'undefined') return null;

    const versionMatch = navigator.userAgent.match(/\b(?:Chrome|Chromium|CriOS|Edg)\/(\d+)\./);
    const majorVersion = versionMatch?.[1] ? Number(versionMatch[1]) : null;

    return Number.isFinite(majorVersion) ? majorVersion : null;
};

const shouldUseGlassPinFallback = () => {
    const chromiumMajorVersion = getChromiumMajorVersion();

    return (
        chromiumMajorVersion !== null &&
        chromiumMajorVersion <= LEGACY_CHROMIUM_GLASS_MARKER_MAX_MAJOR_VERSION
    );
};

const getMarkerLabelHeight = (marker: ContourMarkerItem) => {
    if (marker.labelTitle) return DETAILED_LABEL_HEIGHT;
    if (marker.label) return LABEL_HEIGHT;

    return 0;
};

const PinShape = ({
                      width,
                      height,
                      backgroundColor,
                      borderColor,
                      strokeWidth,
                      isGlass,
                      useGlassFallback,
                  }: {
    width: number;
    height: number;
    backgroundColor: string;
    borderColor: string;
    strokeWidth: number;
    isGlass: boolean;
    useGlassFallback: boolean;
}) => {
    const rawId = useId();
    const safeId = rawId.replace(/[^a-zA-Z0-9_-]/g, '');
    const shadowFilterId = `map-marker-shadow-${safeId}`;
    const glassFillGradientId = `map-marker-glass-fill-${safeId}`;
    const glassHighlightGradientId = `map-marker-glass-highlight-${safeId}`;
    const scaleX = width / FIGMA_PIN_VISIBLE_WIDTH;
    const scaleY = height / FIGMA_PIN_VISIBLE_HEIGHT;
    const offsetX = -FIGMA_PIN_VISIBLE_LEFT * scaleX;
    const offsetY = -FIGMA_PIN_VISIBLE_TOP * scaleY;
    const pinLayerTransform = `translate(${offsetX}px, ${offsetY}px) scale(${scaleX}, ${scaleY})`;
    const normalizedBorderColor = borderColor === 'unset' ? 'none' : borderColor;
    const pinLayerSx = {
        position: 'absolute',
        top: 0,
        left: 0,
        width: `${FIGMA_PIN_WIDTH}px`,
        height: `${FIGMA_PIN_HEIGHT}px`,
        transform: pinLayerTransform,
        transformOrigin: 'top left',
        pointerEvents: 'none',
    };

    return (
        <>
            {isGlass && !useGlassFallback ? (
                <Box
                    sx={{
                        ...pinLayerSx,
                        backgroundColor,
                        backdropFilter: 'blur(6px)',
                        WebkitBackdropFilter: 'blur(6px)',
                        maskImage: FIGMA_PIN_MASK_IMAGE,
                        WebkitMaskImage: FIGMA_PIN_MASK_IMAGE,
                        maskRepeat: 'no-repeat',
                        WebkitMaskRepeat: 'no-repeat',
                        maskSize: '100% 100%',
                        WebkitMaskSize: '100% 100%',
                    }}
                />
            ) : null}
            <Box
                component="svg"
                viewBox={`0 0 ${FIGMA_PIN_WIDTH} ${FIGMA_PIN_HEIGHT}`}
                preserveAspectRatio="none"
                sx={{
                    ...pinLayerSx,
                    display: 'block',
                    overflow: 'visible',
                }}
            >
                {isGlass ? (
                    <defs>
                        {useGlassFallback ? (
                            <>
                                <linearGradient
                                    id={glassFillGradientId}
                                    x1="21"
                                    y1="10"
                                    x2="50"
                                    y2="58"
                                    gradientUnits="userSpaceOnUse"
                                >
                                    <stop offset="0" stopColor="#FFFFFF" stopOpacity="0.78"/>
                                    <stop offset="0.48" stopColor="#FFFFFF" stopOpacity="0.48"/>
                                    <stop offset="1" stopColor="#FFFFFF" stopOpacity="0.36"/>
                                </linearGradient>
                                <radialGradient
                                    id={glassHighlightGradientId}
                                    cx="0"
                                    cy="0"
                                    r="1"
                                    gradientUnits="userSpaceOnUse"
                                    gradientTransform="translate(29 17) rotate(58) scale(30 18)"
                                >
                                    <stop offset="0" stopColor="#FFFFFF" stopOpacity="0.7"/>
                                    <stop offset="0.62" stopColor="#FFFFFF" stopOpacity="0.18"/>
                                    <stop offset="1" stopColor="#FFFFFF" stopOpacity="0"/>
                                </radialGradient>
                            </>
                        ) : null}
                        <filter
                            id={shadowFilterId}
                            x="0"
                            y={FIGMA_PIN_FILTER_Y}
                            width={FIGMA_PIN_WIDTH}
                            height={FIGMA_PIN_FILTER_HEIGHT}
                            filterUnits="userSpaceOnUse"
                            colorInterpolationFilters="sRGB"
                        >
                            <feOffset in="SourceAlpha" dy="8" result="offset"/>
                            <feGaussianBlur in="offset" stdDeviation="8" result="blur"/>
                            <feFlood floodColor="#000000" floodOpacity="0.25" result="color"/>
                            <feComposite in="color" in2="blur" operator="in" result="shadow"/>
                            <feMerge>
                                <feMergeNode in="shadow"/>
                            </feMerge>
                        </filter>
                    </defs>
                ) : null}
                <path
                    d={FIGMA_PIN_PATH}
                    fill={useGlassFallback ? `url(#${glassFillGradientId})` : backgroundColor}
                    stroke="none"
                    filter={isGlass ? `url(#${shadowFilterId})` : undefined}
                    style={{
                        filter: isGlass ? undefined : FIGMA_PIN_SHADOW,
                    }}
                />
                {isGlass && useGlassFallback ? (
                    <>
                        <path
                            d={FIGMA_PIN_PATH}
                            fill={`url(#${glassHighlightGradientId})`}
                            stroke="none"
                        />
                        <path
                            d={FIGMA_PIN_PATH}
                            fill="transparent"
                            stroke="rgb(255 255 255 / 0.55)"
                            strokeWidth="1.2"
                        />
                    </>
                ) : null}
                <path
                    d={FIGMA_PIN_PATH}
                    fill="transparent"
                    stroke={normalizedBorderColor}
                    strokeWidth={normalizedBorderColor === 'none' ? 0 : strokeWidth}
                />
            </Box>
        </>
    );
};

const MarkerContent = ({
                           marker,
                           active,
                           config,
                           bodyTop,
                       }: {
    marker: ContourMarkerItem;
    active: boolean;
    config: MarkerSizeConfig;
    bodyTop: number;
}) => {
    const {palette} = useTheme();
    const backgroundColor =
        marker.backgroundColor ?? (active ? palette.accent : palette.white_100);
    const borderColor =
        marker.borderColor ?? (active ? palette.white_100 : palette.accent);
    const isGlassPin = !active && backgroundColor === palette.white_50;
    const useGlassFallback = isGlassPin && shouldUseGlassPinFallback();
    const iconColor = marker.iconColor ?? palette.dark;
    const labelHeight = getMarkerLabelHeight(marker);
    const content =
        active && marker.activeContent
            ? marker.activeContent
            : marker.icon ?? <PlusIcon/>;

    return (
        <>
            {labelHeight ? (
                <Box
                    className="map-marker-label"
                    sx={{
                        position: 'absolute',
                        top: -8,
                        left: '50%',
                        transform: 'translateX(-50%)',
                        zIndex: 2,
                        height: labelHeight,
                        backgroundColor: active ? palette.accent : palette.backgroundColor,
                        color: active ? palette.white_100 : palette.dark,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        maxWidth: 240,
                        px: 2,
                        py: 1,
                        borderRadius: '24px',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        // transition: 'background-color 160ms ease, color 160ms ease',
                    }}
                >
                    {marker.labelTitle ? (
                        <Typography
                            variant="subtitle_14_20"
                            color="inherit"
                            sx={{
                                maxWidth: '100%',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                            }}
                        >
                            {marker.labelTitle}
                        </Typography>
                    ) : null}

                    {marker.label ? (
                        <Typography
                            variant="small_description"
                            color="inherit"
                            sx={{
                                maxWidth: '100%',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                            }}
                        >
                            {marker.label}
                        </Typography>
                    ) : null}
                </Box>
            ) : null}

            <Box
                sx={{
                    position: 'absolute',
                    top: bodyTop,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    zIndex: 1,
                    width: config.bodyWidth,
                    height: config.bodyHeight,
                }}
            >
                <PinShape
                    width={config.bodyWidth}
                    height={config.bodyHeight}
                    backgroundColor={backgroundColor}
                    borderColor={borderColor}
                    strokeWidth={config.strokeWidth}
                    isGlass={isGlassPin}
                    useGlassFallback={useGlassFallback}
                />
            </Box>

            <Box
                sx={{
                    position: 'absolute',
                    top: bodyTop + config.iconTop,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    display: 'grid',
                    placeItems: 'center',
                    lineHeight: 1,
                    zIndex: 2,
                    width: 24,
                    height: 24,
                    color: iconColor,
                    '& svg': {
                        width: 24,
                        height: 24,
                        color: iconColor,
                    },
                }}
            >
                {content}
            </Box>
        </>
    );
};

export const ContourMarker = memo(
    ({
         marker,
         active = false,
         elevated = active,
         onClick,
         onMouseEnter,
         onMouseLeave,
         onDragStart,
         onDragEnd,
     }: ContourMarkerProps) => {
        const isDisabled = Boolean(marker.isDisabled);
        const isDraggable = Boolean(marker.isDraggable) && !isDisabled;
        const size = marker.size ?? 'medium';
        const config = MARKER_SIZE_CONFIG[size];
        const labelHeight = getMarkerLabelHeight(marker);
        const bodyTop = labelHeight ? labelHeight - config.labelOverlap : 0;
        const markerHeight = bodyTop + config.bodyHeight;
        const handleAdvancedMarkerRef = useCallback(
            (advancedMarker: google.maps.marker.AdvancedMarkerElement | null) => {
                const markerElement = advancedMarker?.element;

                if (!markerElement) return;

                markerElement.style.setProperty('outline', 'none');
                markerElement.style.setProperty('box-shadow', 'none');
            },
            [],
        );

        return (
            <AdvancedMarker
                ref={handleAdvancedMarkerRef}
                position={marker.position}
                clickable={!isDisabled}
                draggable={isDraggable}
                zIndex={elevated ? 1000 : 10}
                onClick={() => {
                    if (isDisabled) return;

                    onClick?.(marker);
                }}
                onMouseEnter={() => {
                    if (isDisabled) return;

                    onMouseEnter?.(marker);
                }}
                onMouseLeave={() => {
                    if (isDisabled) return;

                    onMouseLeave?.(marker);
                }}
                onDragStart={() => {
                    if (!isDraggable) return;

                    onDragStart?.({
                        marker,
                        from: {...marker.position},
                    });
                }}
                onDragEnd={(event) => {
                    if (!isDraggable) return;

                    onDragEnd?.({
                        marker,
                        from: {...marker.position},
                        droppedAt: event.latLng?.toJSON?.() ?? null,
                    });
                }}
            >
                <Box
                    className="map-marker-root"
                    sx={{
                        position: 'relative',
                        width: config.bodyWidth,
                        height: markerHeight,
                        cursor: isDisabled ? 'default' : isDraggable ? 'grab' : 'pointer',
                        opacity: isDisabled ? 0.7 : 1,
                        userSelect: 'none',
                        // transition: 'opacity 180ms ease',
                        '&:active': {
                            cursor: isDisabled ? 'default' : isDraggable ? 'grabbing' : 'pointer',
                        },
                    }}
                >
                    <MarkerContent
                        marker={marker}
                        active={active}
                        config={config}
                        bodyTop={bodyTop}
                    />
                </Box>
            </AdvancedMarker>
        );
    },
);
